// src/components/RentalDateSelector/RentalDateSelector.jsx
import React from "react";
import PropTypes from "prop-types";
import { useBooking } from "../../context/BookingContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const todayISO = new Date().toISOString().substring(0, 10);

const getDateInputValue = (value) => {
  if (!value) return "";
  return String(value).substring(0, 10);
};

const getTimePart = (isoStr, fallback = "12:00:00") => {
  if (!isoStr) return fallback;

  const date = new Date(isoStr);

  if (Number.isNaN(date.getTime())) {
    return fallback;
  }

  return date.toTimeString().split(" ")[0] || fallback;
};

const createISODateWithTime = (dateValue, timePart = "12:00:00") => {
  return new Date(`${dateValue}T${timePart}`).toISOString();
};

const RentalDateSelector = ({ onDatesChange, isDisabled, labelColor }) => {
  const { selectedDates, setSelectedDates } = useBooking();

  const startInputValue = getDateInputValue(selectedDates?.startDate);
  const endInputValue = getDateInputValue(selectedDates?.endDate);

  const handleDateChange = (event) => {
    const { name, value } = event.target;

    if (!value) return;

    const selected = new Date(value);
    selected.setHours(12, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selected < today) {
      toast.error("Et voi valita menneitä päivämääriä.");
      return;
    }

    if (name === "startDate") {
      const timePart = getTimePart(selectedDates?.startDate);
      const newStartISO = createISODateWithTime(value, timePart);

      const newEnd = new Date(newStartISO);
      newEnd.setDate(newEnd.getDate() + 1);

      const updated = {
        startDate: newStartISO,
        endDate: newEnd.toISOString(),
      };

      setSelectedDates(updated);

      if (onDatesChange) {
        onDatesChange(updated);
      }
    }

    if (name === "endDate") {
      const currentStartValue = getDateInputValue(selectedDates?.startDate);

      if (currentStartValue && value < currentStartValue) {
        toast.error("Loppupäivä ei voi olla ennen aloituspäivää.");
        return;
      }

      const timePart = getTimePart(selectedDates?.endDate);
      const newEndISO = createISODateWithTime(value, timePart);

      const updated = {
        startDate: selectedDates?.startDate,
        endDate: newEndISO,
      };

      setSelectedDates(updated);

      if (onDatesChange) {
        onDatesChange(updated);
      }
    }
  };

  const getLabelClasses = () =>
    "mb-2 block font-customFont1 text-sm font-extrabold uppercase tracking-[0.1em] !text-[#111827] dark:!text-white";

  const inputClassName = `
    w-full rounded-2xl border border-[#DDE7E5] bg-white/90
    px-4 py-3 font-customFont1 text-base font-semibold text-[#111827]
    shadow-sm outline-none transition
    focus:border-primary focus:ring-2 focus:ring-primary/30
    disabled:cursor-not-allowed disabled:opacity-60
    dark:border-darkborder dark:bg-darksurface dark:text-white
  `;

  return (
    <div
      className="
        grid grid-cols-1 gap-4
        sm:grid-cols-2
      "
    >
      <div className="flex flex-col">
        <label
          htmlFor="startDate"
          className={getLabelClasses()}
          style={labelColor ? { color: labelColor } : {}}
        >
          Alkaa
        </label>

        <input
          id="startDate"
          name="startDate"
          type="date"
          value={startInputValue}
          min={todayISO}
          onChange={handleDateChange}
          disabled={isDisabled}
          className={inputClassName}
        />
      </div>

      <div className="flex flex-col">
        <label
          htmlFor="endDate"
          className={getLabelClasses()}
          style={labelColor ? { color: labelColor } : {}}
        >
          Loppuu
        </label>

        <input
          id="endDate"
          name="endDate"
          type="date"
          value={endInputValue}
          min={startInputValue || todayISO}
          onChange={handleDateChange}
          disabled={isDisabled}
          className={inputClassName}
        />
      </div>
    </div>
  );
};

RentalDateSelector.propTypes = {
  onDatesChange: PropTypes.func,
  isDisabled: PropTypes.bool,
  labelColor: PropTypes.string,
};

export default RentalDateSelector;